import classNames from 'classnames';
import React from 'react';

import { Responsive, responsiveClasses, withBaseDefault } from '../helpers/responsive';
import {
  FLEX_WIDTH, FLEX_GAP, FLEX_ALIGN_ITEMS, FLEX_ALIGN_SELF, FLEX_DIRECTION, FLEX_ORDER,
} from '../../styles/generated/data/types';

// Note: Property values and options need to stay in sync with `classes-utility/flex.utilities.json`
// (and the display values with `classes-utility/display.scss`). RCFlex.test.tsx has a
// drift-guard that asserts each value below maps to a generated class.
// `direction`, `align`, `gap`, `width`, and `order` are imported from the generated source so they
// can't drift; `justify` (short-label vs CSS-value vocab mismatch) and the display values are
// hand-maintained.
export const FLEX_DIRECTIONS = FLEX_DIRECTION;
/** Container alignment (`align-items`, `fx-align-*`). Excludes `auto`, which only `align-self` accepts. */
export const FLEX_ALIGNMENTS = FLEX_ALIGN_ITEMS;
/** RCFlex.Item alignment (`align-self`, `fx-align-self-*`). Adds `auto` to the container's set. */
export const FLEX_SELF_ALIGNMENTS = FLEX_ALIGN_SELF;
export const FLEX_JUSTIFICATIONS = ['start', 'center', 'end', 'space-between', 'space-around', 'space-evenly'] as const;
/** Spacing-scale values (incl. the grid `gutter`) with `gap` / `row-gap` / `column-gap` classes. */
export const GAP_SCALE = FLEX_GAP;
/** RCFlex.Item width values (`fx-width-*`). */
export const FLEX_WIDTHS = FLEX_WIDTH;
/** RCFlex.Item order values (`fx-order-*`). */
export const FLEX_ORDERS = FLEX_ORDER;
/** The container's display mode (`d-*`). Both flex contexts, plus `block` to leave flex. */
export const FLEX_DISPLAYS = ['flex', 'inline-flex', 'block'] as const;

type Direction = typeof FLEX_DIRECTIONS[number];
// `auto` is only valid for `align-self` (the Item), not `align-items` (the container). The two
// vocabularies are separate groups in `flex.utilities.json`, so that distinction is enforced at
// the source rather than patched here.
type Align = typeof FLEX_ALIGNMENTS[number];
type SelfAlign = typeof FLEX_SELF_ALIGNMENTS[number];
type Justify = typeof FLEX_JUSTIFICATIONS[number];
type GapScale = typeof GAP_SCALE[number];
type Width = typeof FLEX_WIDTHS[number];
type Order = typeof FLEX_ORDERS[number];
type Display = typeof FLEX_DISPLAYS[number];
/** The spacing scale (which now includes the grid `gutter`) — valid for gap / row-gap / column-gap at every breakpoint. */
type GapValue = GapScale;

// `space-between` → `between`, etc. Base flex value → utility-class suffix.
function justifySuffix(value: Justify): string {
  return value.includes('-') ? value.split('-')[1] : value;
}

// Typed props map to the utility classes; `className` / `...rest` are the escape hatch
// for styling the typed interface doesn't cover. `...rest` forwards HTML attributes and
// `ref` to the underlying element.
/* eslint-disable react/require-default-props, react/jsx-props-no-spreading */

type RCFlexProps = React.ComponentPropsWithoutRef<'div'> & {
  /** The direction items should flow. Defaults to `row`. Pass a breakpoint object to vary it, e.g. `{ desktop: 'row', tablet: 'col' }`. */
  direction?: Responsive<Direction>;
  /** The container's display mode. Defaults to `flex`. Use a breakpoint object to leave flex at a size, e.g. `{ mobile: 'block' }`.
   *  To *stack* items, prefer `direction={{ mobile: 'col' }}` (keeps `gap`); `display: 'block'` leaves the flex context entirely, so
   *  `gap` and the other flex props stop applying there. */
  display?: Responsive<Display>;
  /** Spacing between items (both axes). No default — unset means no gap (CSS initial `0`).
   *  `gutter` matches the grid gutter. Pass a breakpoint object to vary it. */
  gap?: Responsive<GapValue>;
  /** Spacing between items on the block (row) axis; overrides `gap` vertically. Pass a breakpoint object to vary it. */
  rowGap?: Responsive<GapValue>;
  /** Spacing between items on the inline (column) axis; overrides `gap` horizontally. Pass a breakpoint object to vary it. */
  columnGap?: Responsive<GapValue>;
  /** Controls whether items can wrap to a new line when needed. Pass a breakpoint object to vary it. */
  wrap?: Responsive<boolean>;
  /** Controls alignment of items along the "main axis" – horizontal when a `row` and vertical in a `col`. Pass a breakpoint object to vary it. */
  justify?: Responsive<Justify>;
  /** Controls alignment of items along the "cross-axis" – vertical in a `row` and horizontal in a `col`. Pass a breakpoint object to vary it. */
  align?: Responsive<Align>;
  /** When this element is a child of another flex container, controls whether this item should grow to fill empty space in its container.
   *  Defaults to `false` (flex-grow: 0). Items in a container with the same grow value grow at the same rate. */
  grow?: RCFlexItemProps['grow'];
};

/**
 * Utility component — builds a flex layout through `d-flex` / `fx-*` / `gap-*` utility classes
 * via a constrained prop API. No structural contract.
 *
 * A set of components allowing you to build a flex layout without writing styles or using CSS classes.
 *
 * Layout props correspond to our `fx-` / `d-` utility classes and are responsive: pass a plain value,
 * or a `{ desktop, tablet, mobile }` object to vary by breakpoint — prefer this over `useMediaQuery`
 * for styling (see Component Conventions #7). For styling the typed props don't cover (padding, color,
 * margin, …), use `className` / HTML-attribute passthrough; don't route responsive *layout* through it.
 *
 * As you build flex layouts, make sure to use as few properties as possible to minimize unexected quirks & side effects. [The CSS-Tricks](https://css-tricks.com/snippets/css/a-guide-to-flexbox/) flex article is always useful to keep on hand.
*/
const RCFlexComponent = React.forwardRef<HTMLDivElement, RCFlexProps>(
  function RCFlex({
    direction = 'row',
    display = 'flex',
    gap = undefined,
    rowGap = undefined,
    columnGap = undefined,
    wrap = false,
    align = 'stretch',
    justify = 'start',
    grow = false,
    className,
    children,
    ...rest
  }, ref) {
    return (
      <div
        ref={ref}
        className={classNames(
          // display's default (flex) differs from the CSS initial (block), so a partial
          // object keeps the base as flex rather than resetting it (see withBaseDefault).
          responsiveClasses(withBaseDefault(display, 'flex'), (v, prefix) => `${prefix}d-${v}`),
          // `prefix` is '' at the base/desktop tier; defaults are omitted there but always
          // emitted at tablet/mobile so they can override the base via the cascade.
          responsiveClasses(direction, (v, prefix) => (!!prefix || v !== 'row') && `${prefix}fx-dir-${v}`),
          responsiveClasses(align, (v, prefix) => (!!prefix || v !== 'stretch') && `${prefix}fx-align-${v}`),
          responsiveClasses(justify, (v, prefix) => (!!prefix || v !== 'start') && `${prefix}fx-justify-${justifySuffix(v)}`),
          responsiveClasses(gap, (v, prefix) => `${prefix}gap-${v}`),
          responsiveClasses(rowGap, (v, prefix) => `${prefix}row-gap-${v}`),
          responsiveClasses(columnGap, (v, prefix) => `${prefix}column-gap-${v}`),
          // boolean props: the "off" class is only needed at a breakpoint (to override the base)
          responsiveClasses(wrap, (v, prefix) => (v ? `${prefix}fx-wrap` : prefix && `${prefix}fx-nowrap`)),
          responsiveClasses(grow, (v, prefix) => (v ? `${prefix}fx-grow` : prefix && `${prefix}fx-nogrow`)),
          className,
        )}
        {...rest}
      >
        {children}
      </div>
    );
  },
);

type RCFlexItemProps = React.ComponentPropsWithoutRef<'div'> & {
  /** Controls whether this item should grow to fill empty space in its container. Defaults to `false` (flex-grow: 0).
   *  Items in a container with the same grow value grow at the same rate. Pass a breakpoint object to vary it. */
  grow?: Responsive<boolean>;
  /** Controls whether this item should shrink when the container runs out of space. Defaults to `true` (flex-shrink: 1).
   *  Items in a container with the same shrink value will shrink at the same rate. Pass a breakpoint object to vary it. */
  shrink?: Responsive<boolean>;
  /** Allows for locking this item to a fixed percentage width (or `auto`). Pass a breakpoint object to vary it. */
  width?: Responsive<Width>;
  /** Controls the visual order of the item in the layout. By default, items have an order value of 0.
   *  Items are organized numerically and then by DOM order when numbers are identical. Pass a breakpoint object to vary it.
   *
   * _Note: Use with caution. The visual order does not change the DOM (i.e keyboard focus) order._ */
  order?: Responsive<Order>;
  /** Allows this item to override the parent's alignment. `auto` falls back to the container's
   *  `align`. Pass a breakpoint object to vary it. */
  align?: Responsive<SelfAlign>;
};

const RCFlexItemComponent = React.forwardRef<HTMLDivElement, RCFlexItemProps>(
  function RCFlexItem({
    grow = false,
    shrink = undefined,
    width = undefined,
    order = '0',
    align = undefined,
    className,
    children,
    ...rest
  }, ref) {
    return (
      <div
        ref={ref}
        className={classNames(
          responsiveClasses(grow, (v, prefix) => (v ? `${prefix}fx-grow` : prefix && `${prefix}fx-nogrow`)),
          // shrink is undefined by default (no class); false → noshrink at every tier it's set on
          responsiveClasses(shrink, (v, prefix) => (v ? `${prefix}fx-shrink` : `${prefix}fx-noshrink`)),
          responsiveClasses(width, (v, prefix) => `${prefix}fx-width-${v}`),
          responsiveClasses(order, (v, prefix) => (!!prefix || v !== '0') && `${prefix}fx-order-${v}`),
          responsiveClasses(align, (v, prefix) => `${prefix}fx-align-self-${v}`),
          className,
        )}
        {...rest}
      >
        {children}
      </div>
    );
  },
);

export const RCFlex = Object.assign(RCFlexComponent, { Item: RCFlexItemComponent });
